import React, { useState, useEffect, useRef } from "react";
import { useManualQuery } from "graphql-hooks";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Button,
  Divider,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem,
  Grow,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import UserCard from "./UserCard";
import FollowButton from "./FollowButton";
import AlertPopup from "./AlertPopup";
import LikesPopup from "./LikesPopup";

import { 
  CURRENT_USER_DATA_BY_EMAIL 
} from "../graphQL/user_qry";

const CommentBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  padding: theme.spacing(0, 0),
}));

const StyledDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "size" && prop !== "color" && prop !== "border",
})(({ theme, size = 8, color = theme.palette.primary.main, border }) => ({
  width: size,
  height: size,
  borderRadius: "80%",
  backgroundColor: color,  
}));

const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const TruncatedTitle = ({ title, maxWidth = 380 }) => {
  const textRef = useRef(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    }
  }, [title]);

  const textElement = (
    <Typography
      ref={textRef}
      fontWeight="bold"
      noWrap
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth,
        display: "block",
      }}
    >
      {title}
    </Typography>
  );

  return isOverflowed ? (
    <StyledTooltip title={title}>{textElement}</StyledTooltip>
  ) : (
    textElement
  );
};

export default function CommentDialog({
  open,
  onClose,
  product,
  comments = [],
  onAddComment,
  onAddReply,
  onFollowChange,
  onCommentLikeToggle,
  onCommentReplyLikeToggle,
  onDeleteComment,
  onDeleteReply,
  onEditComment,
  onEditReply
}) {

  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:833px)");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [commentEmojiIndex, setCommentEmojiIndex] = useState(0);
  const [replyEmojiIndex, setReplyEmojiIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editText, setEditText] = useState("");
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loggedUserAvatar, setLoggedUserAvatar] = useState("");
  const visibleCommentEmojiCount = 4;
  const visibleReplyEmojiCount = 3;

  const emojis = ["❤️", "😊", "😮", "😢", "👍", "👎", "💯", "👏",];
  const editInputRef = useRef(null);

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;
  let loggedUserName = localStorage.getItem("userName") 
              ? localStorage.getItem("userName") 
              : null;
  let userEmail = localStorage.getItem("email") ? localStorage.getItem("email") : null;

  const [getCurrUsrData] = useManualQuery(CURRENT_USER_DATA_BY_EMAIL);

  useEffect(() => {
    (async () => setCurrentUserInfo())();
  }, [userEmail]);

  const setCurrentUserInfo = async () => {

    if (!userEmail) return;
    const {
      loading: loadingCurrUsrInfo,
      error: errorCurrUsrInfo,
      data: currUsrInfo,
    } = await getCurrUsrData({
      variables: {
        email: userEmail,
      },
    });
    if (!loadingCurrUsrInfo && currUsrInfo) {
      setLoggedUserAvatar(
        currUsrInfo.currentUserDataByEmail.avatarFileName
      );
    }
  };  

  const commentsEndRef = useRef(null);
  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };  

  const replyInputRefs = useRef({});
  useEffect(() => {
    if (replyingTo && replyInputRefs.current[replyingTo]) {
      replyInputRefs.current[replyingTo].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Put cursor inside the reply TextField
      setTimeout(() => {
        replyInputRefs.current[replyingTo]?.focus();
      }, 50);
    }
  }, [replyingTo]);

  const visibleCommentEmojis = emojis.slice(
    commentEmojiIndex,
    commentEmojiIndex + visibleCommentEmojiCount
  );

  const visibleReplyEmojis = emojis.slice(
    replyEmojiIndex,
    replyEmojiIndex + visibleReplyEmojiCount
  );

  const handleNextCommentEmoji = () => {
    if (commentEmojiIndex + visibleCommentEmojiCount < emojis.length) {
      setCommentEmojiIndex(commentEmojiIndex + 1);
    }
  };

  const handlePrevCommentEmoji = () => {
    if (commentEmojiIndex > 0) {
      setCommentEmojiIndex(commentEmojiIndex - 1);
    }
  };

  const handleNextReplyEmoji = () => {
    if (replyEmojiIndex + visibleReplyEmojiCount < emojis.length) {
      setReplyEmojiIndex(replyEmojiIndex + 1);
    }
  };

  const handlePrevReplyEmoji = () => {
    if (replyEmojiIndex > 0) {
      setReplyEmojiIndex(replyEmojiIndex - 1);
    }
  };

  const safeDecode = (text) => {
    try {
      return decodeURIComponent(text);
    } catch {
      return text;
    };
  }

  const timeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);

    const diffMs = now - past;
    const diffSeconds = Math.floor(diffMs / 1000);

    const units = [
      { label: "y", seconds: 60 * 60 * 24 * 365 },
      { label: "w", seconds: 60 * 60 * 24 * 7 },
      { label: "d", seconds: 60 * 60 * 24 },
      { label: "h", seconds: 60 * 60 },
      { label: "m", seconds: 60 },
      { label: "s", seconds: 1 },
    ];

    for (const unit of units) {
      const value = Math.floor(diffSeconds / unit.seconds);
      if (value >= 1) {
        return `${value}${unit.label}`;
      }
    }
    return "just now";
  };

  const handlePost = () => {
    onAddComment(commentText);
    setCommentText("");
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  };

  const handleReplyPost = (commentUuid) => {
    onAddReply(replyText, commentUuid);
    setReplyText("");
    setReplyingTo(null);
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/${user.userName}/home`);
  };

  const handleCommentEmojiClick = (emoji, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCommentText(prev => {
      const updated = prev + emoji;

      setTimeout(() => {
        const el = document.activeElement;
        if (el && el.setSelectionRange) {
          const len = updated.length;
          el.setSelectionRange(len, len);
        }
      }, 0);

      return updated;
    });
    e.currentTarget.blur();
  };

  const handleReplyEmojiClick = (emoji, e) => {
    e.preventDefault();
    e.stopPropagation();
    setReplyText(prev => {
      const updated = prev + emoji;

      setTimeout(() => {
        const el = replyInputRefs.current[replyingTo];
        if (el && el.setSelectionRange) {
          const len = updated.length;
          el.setSelectionRange(len, len);
        }
      }, 0);

      return updated;
    });
    e.currentTarget.blur();
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          width: isMobile ? "373px" : "600px",
          height: "95%",
          maxHeight: "100vh",
        },
      }}
    >
      <DialogContent 
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 1,
            borderBottom: "1px solid #e1e1e1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            width: isMobile ? "373px" : "600px",
            height: "56px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "left",
            }}
          >
            <IconButton
              size="small"
              sx={{
                backgroundColor: "#F5F5F5",
                p: 1.5,
              }}>
              <ModeCommentOutlinedIcon 
                sx={{ 
                  width: 16,
                  height: 16 
                }}
              />
            </IconButton>
            <Typography 
              fontWeight="bold"
              sx={{ 
                pl: 1,
                pt: 0.75, 
              }} 
            >
              Comment
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Product Summary */}
        <Box
          sx={{
            display: "flex",
            borderRadius: "8px",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            py: isMobile ? "16px" : "8px",
            pl: isMobile ? "2px" : "16px",
            m: "16px",
            backgroundColor: "#FFF1F4",
            width: isMobile ? "291px" : "568px",
            height: isMobile ? "333px" : "177px",
          }}
        >
          <Avatar
            variant="square"
            src={product.toyImages && product.toyImages[0]
                  ? product.toyImages[0].fileName
                  : product.img
                }
            sx={{ 
              width: 140, 
              height: 140, 
              borderRadius: "4px" 
            }}
          />
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderRadius: "8px",
                border: "1px solid #e1e1e1",
                flexDirection: "row",
                alignItems: "center",                
                backgroundColor: "#fff",
                width: isMobile ? "279px" : "380px",
                height: "56",
                p: "8px",
                mx: isMobile ? "4px" : "16px",
                mt: isMobile ? "16px" : "8px",
                mb: isMobile ? "8px" : "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                }}
              >
                <UserCard
                  user={product.userId}
                  onUsernameClick={handleUserProfileNavigation}
                  onFullNameClick={handleUserProfileNavigation}
                  type="avatar"
                  context="card"
                >
                  <Avatar src={
                    product.userId
                    && product.userId.avatarFileName} 
                  />
                </UserCard>
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                  }}
                >
                  <UserCard
                    user={product.userId}
                    onUsernameClick={handleUserProfileNavigation}
                    onFullNameClick={handleUserProfileNavigation}
                    type="username"
                    context="card"
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {product.userId ? product.userId.username : ""}
                    </Typography>
                  </UserCard>
                  <Typography variant="caption" color="text.secondary">
                    {/* Should be updated in the future with user last login or last activity */}
                    {/* {product.userId ? product.userId.time : ""}*/}
                  </Typography>
                </Box>
              </Box>
              {product.userId.userUuid !== userUuid && (
                <FollowButton
                  isFollowing={product.userId ? product.userId.isFollowing : ""}
                  onToggle={onFollowChange}
                />
              )}
            </Box>
            <Box
              sx= {{
                mx: isMobile ? "8px" : "16px",
                width: isMobile ? "259px" : "380px",
              }}
            >
              <TruncatedTitle title={product.title}>
                <Typography 
                  fontWeight="bold"
                  noWrap
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: isMobile ? 259 : 380,
                    display: "block",
                  }}
                >
                  {product.title}
                </Typography>
              </TruncatedTitle>
              <Typography 
                variant="caption" 
                color="text.secondary"
              >
                {product.category}
              </Typography>
              <Box 
                sx={{ 
                  display: "flex", 
                  flexDirection: "row",
                  pt: isMobile? 0.5 : 1,
                }}
              >
                {product.price && (
                  <Typography 
                    variant="h6"
                    color="green"
                    fontWeight="bold"
                  >
                    ${parseFloat(product.price || 0).toFixed(2)}
                  </Typography>
                )}
                {product.oldPrice && (
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ 
                      textDecoration: "line-through", 
                      color: "#999",
                      pl: 2,
                    }}
                  >
                    ${parseFloat(product.oldPrice || 0).toFixed(2)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>          
        </Box>

        {/* Comments List */}
        <Box
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            overflowX: "hidden", 
            p: 2,
          }}
        >
          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to share your thoughts!
            </Typography>
          ) : (
            comments.length && comments.map((c, index) => (
              <Box key={c.uuid} sx={{ width: "100%" }}>
                <CommentBox key={c.uuid}>
                  <Box sx={{ display: "flex",flexDirection: "column" }}>
                    <Box sx={{ display: "flex",flexDirection: "row" }}>
                      <Avatar 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                        }}
                        src={c.userId.avatarFileName} 
                      />
                      <Box sx={{ pl: 1 }} >
                        <Typography 
                          variant="subtitle2"
                        >
                           {c.userId.username} {c.userId.username === loggedUserName && "(you)"}
                        </Typography>
                        {editTarget?.type === "comment" && editTarget.commentUuid === c.uuid ? (
                          <Box
                            sx={{
                              display: "flex",
                              borderRadius: "8px",
                              flexDirection: "row",
                              alignItems: isMobile ? "flex-start" : "center",
                              p: "8px",
                              mt: 0.5,
                              backgroundColor: "#F5F5F5",
                              width: isMobile ? "211px" : "488px",
                              position: "relative", 
                            }}
                          >
                            <TextField                          
                              size="small"
                              multiline
                              value={editText}                          
                              onChange={(e) => setEditText(safeDecode(e.target.value))}
                              InputProps={{
                                inputRef: editInputRef,
                              }}
                              onKeyDown={(e) => {
                                // Ctrl+Enter or Cmd+Enter = SAVE
                                if ((e.key === "Enter" && e.ctrlKey) || (e.key === "Enter" && e.metaKey)) {
                                  e.preventDefault();
                                  if (editTarget.type === "comment") {
                                    onEditComment(editTarget.commentUuid, editText);
                                  } else {
                                    onEditReply(editTarget.commentUuid, editTarget.replyUuid, editText);
                                  }
                                  setEditTarget(null);
                                }
                                // Otherwise allow Enter to insert newline (do NOT preventDefault)
                              }}
                              sx={{ 
                                width: isMobile ? "241px" : "518px",                           
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  backgroundColor: "#F5F5F5",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "& .MuiInputBase-input": {
                                  paddingBottom: "48px",
                                },                                
                              }}
                            />
                            {!isMobile ? (
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: "6px",
                                  left: "10px",
                                  display: "flex",
                                  gap: 1,
                                  zIndex: 20,
                                }}
                              >
                                {emojis.map((emoji) => (
                                  <IconButton
                                    key={emoji}
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditText(prev => safeDecode(prev + emoji));
                                    }}
                                    sx={{
                                      p: 0.5,
                                      width: 20,
                                      height: 20,
                                      color: "unset !important",          // remove MUI's gray filter
                                      opacity: 1,                          // force full brightness
                                      "&:hover": {
                                        backgroundColor: "transparent !important",
                                        opacity: 1,
                                      }
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                                      {emoji}
                                    </Typography>
                                  </IconButton>
                                ))}
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: "6px",
                                  ml: "7px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  zIndex: 20,
                                  width: "80%", // prevents covering Post button
                                }}
                              >
                                {/* Left arrow (show only if not at start) */}
                                {replyEmojiIndex > 0 && (
                                  <IconButton size="small" onClick={handlePrevReplyEmoji} sx={{ p: 0 }}>
                                    <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                                      {"<"}
                                    </Typography>
                                  </IconButton>
                                )}

                                {/* Visible 4 emojis */}
                                {visibleReplyEmojis.map((emoji) => (
                                  <IconButton
                                    key={emoji}
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditText(prev => safeDecode(prev + emoji));
                                    }}
                                    sx={{
                                      p: 0.5,
                                      width: 20,
                                      height: 20,
                                      color: "unset !important",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                                      {emoji}
                                    </Typography>
                                  </IconButton>
                                ))}

                                {/* Right arrow (show only if more remain) */}
                                {replyEmojiIndex + visibleReplyEmojiCount < emojis.length && (
                                  <IconButton size="small" onClick={handleNextReplyEmoji} sx={{ p: 0 }}>
                                    <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                                      {">"}
                                    </Typography>
                                  </IconButton>
                                )}
                              </Box>
                            )}                            
                            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                              <StyledTooltip title="Save">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (editTarget.type === "comment") {
                                      onEditComment(editTarget.commentUuid, editText);
                                    } else {
                                      onEditReply(editTarget.commentUuid, editTarget.replyUuid, editText);
                                    }
                                    setEditTarget(null);
                                  }}
                                  sx={{
                                    backgroundColor: "#E6F4EA",
                                    "&:hover": { backgroundColor: "#D4ECD8" },
                                  }}
                                >
                                  <CheckIcon sx={{ fontSize: 14, color: "#1e7e34" }} />
                                </IconButton>
                              </StyledTooltip>
                              <StyledTooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditTarget(null)}
                                  sx={{
                                    backgroundColor: "#F8E6E6",
                                    "&:hover": { backgroundColor: "#F2D5D5" },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 14, color: "#c62828" }} />
                                </IconButton>
                              </StyledTooltip>
                            </Box>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center" 
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                borderRadius: "8px",
                                flexDirection: isMobile ? "column" : "row",
                                alignItems: isMobile ? "flex-start" : "center",
                                p: "8px",
                                mt: 0.5,
                                backgroundColor: "#F5F5F5",
                                width: isMobile ? "230px" : "488px",
                              }}
                            >
                              <Typography
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                                variant="caption" 
                                color="text.primary"
                              >
                                {safeDecode(c.content)}
                              </Typography>
                            </Box>
                            {c.userId.username === loggedUserName && (
                              <IconButton
                                sx={{
                                  padding: "2px",
                                  ml: 1,
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                }}
                                onClick={(e) => {
                                  setSelected({ type: "comment", commentUuid: c.uuid });
                                  setAnchorEl(e.currentTarget);
                                }}
                              >
                                <MoreVertIcon 
                                  sx={{ fontSize: 16 }} 
                                />
                              </IconButton>
                            )}
                          </Box>
                        )}                        
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: "row",
                            alignItems: "center",                
                            backgroundColor: "#fff",                      
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 1,                        
                            }}
                          >
                            <Typography 
                              variant="caption" 
                              color="#9e9e9e"
                              sx={{ mt: 0.5 }}
                            >
                              {timeAgo(c.createDate)}
                            </Typography>
                            <Box sx={{ mt: 1.5 }}> 
                              <StyledDot size={4} color="#E0E0E0" />
                            </Box>
                            <Box sx={{ mt: 0, mr:"auto" }}> 
                              <StyledTooltip
                                title={c.isLiked ? "Click to undo like" : "Click to like"}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => 
                                    onCommentLikeToggle(!c.isLiked, c.uuid)
                                  }
                                  aria-label={
                                    c.isLiked ? "Remove from favorites" : "Add to favorites"
                                  }
                                  sx={{
                                    color: c.isLiked ? "#EF5658" : "#9e9e9e",
                                    p: 0,
                                    pr: 0.5,
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  {c.isLiked ? (
                                    <FavoriteIcon sx={{ fontSize: "14px" }} />
                                  ) : (
                                    <FavoriteBorderOutlinedIcon sx={{ fontSize: "14px" }} />
                                  )}
                                </IconButton>
                              </StyledTooltip>                      
                              <LikesPopup users={c.likedUsers}>
                                <Typography
                                  variant="caption"
                                  sx={{ cursor: "pointer" }}
                                >
                                  {c.likeNo}
                                </Typography>
                              </LikesPopup>
                            </Box>                      
                            <Box sx={{ mt: 1.5 }}> 
                              <StyledDot size={4} color="#E0E0E0" />
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.primary"
                              sx={{
                                cursor: "pointer",
                                "&:hover": { color: "#EF5658" },
                                mt: 0.5,
                              }}
                              onClick={() =>
                                setReplyingTo(replyingTo === c.uuid ? null : c.uuid)
                              }
                            >
                              Reply
                            </Typography>
                          </Box>                    
                        </Box>

                        {/* Render existing replies */}
                        {c.replies && c.replies.length > 0 && (
                          <Box sx={{ ml: 0.5, mt: 3 }}>
                            {c.replies.map((reply) => (
                              <Box 
                                key={reply.uuid} 
                                sx={{ 
                                  display: "flex",
                                  mt: 1,
                                  flexDirection: "row"
                                }}
                              >
                                <Avatar src={reply.userId.avatarFileName} />
                                <Box sx={{ pl: 1 }}>
                                  <Typography 
                                    variant="subtitle2"
                                  >
                                    {reply.userId.username} {reply.userId.username === loggedUserName && "(you)"}
                                  </Typography>
                                  {editTarget?.type === "reply" &&
                                   editTarget.commentUuid === c.uuid &&
                                   editTarget.replyUuid === reply.uuid ? (
                                    <Box
                                      sx={{
                                        backgroundColor: "#F5F5F5",
                                        p: 1,
                                        display: "flex",
                                        flexDirection: "row",
                                        borderRadius: "8px",
                                        width: isMobile ? "193px" : "440px",
                                        alignItems: isMobile ? "flex-start" : "center",
                                        position: "relative",
                                      }}
                                    >
                                      <TextField                          
                                        size="small"
                                        multiline
                                        minRows={1}
                                        value={editText}                          
                                        onChange={(e) => setEditText(safeDecode(e.target.value))}
                                        InputProps={{
                                          inputRef: editInputRef,
                                        }}
                                        onKeyDown={(e) => {
                                          // Ctrl+Enter or Cmd+Enter = SAVE
                                          if ((e.key === "Enter" && e.ctrlKey) || (e.key === "Enter" && e.metaKey)) {
                                            e.preventDefault();
                                            if (editTarget.type === "comment") {
                                              onEditComment(editTarget.commentUuid, editText);
                                            } else {
                                              onEditReply(editTarget.commentUuid, editTarget.replyUuid, editText);
                                            }
                                            setEditTarget(null);
                                          }
                                        }}

                                        sx={{ 
                                          width: isMobile ? "163px" : "410px",                           
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#F5F5F5",
                                          },
                                          "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                          },
                                          "& .MuiInputBase-input": {
                                            paddingBottom: "48px",
                                          },                                
                                        }}
                                      />
                                      {!isMobile ? (
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            bottom: "6px",
                                            left: "10px",
                                            display: "flex",
                                            gap: 1,
                                            zIndex: 20,
                                          }}
                                        >
                                          {emojis.map((emoji) => (
                                            <IconButton
                                              key={emoji}
                                              size="small"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setEditText((prev) => safeDecode(prev + emoji));
                                              }}
                                              sx={{
                                                p: 0.5,
                                                width: 20,
                                                height: 20,
                                                color: "unset !important",          // remove MUI's gray filter
                                                opacity: 1,                          // force full brightness
                                                "&:hover": {
                                                  backgroundColor: "transparent !important",
                                                  opacity: 1,
                                                }
                                              }}
                                            >
                                              <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                                                {emoji}
                                              </Typography>
                                            </IconButton>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            bottom: "6px",
                                            ml: "7px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            zIndex: 20,
                                            width: "80%", // prevents covering Post button
                                          }}
                                        >
                                          {/* Left arrow (show only if not at start) */}
                                          {replyEmojiIndex > 0 && (
                                            <IconButton size="small" onClick={handlePrevReplyEmoji} sx={{ p: 0 }}>
                                              <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                                                {"<"}
                                              </Typography>
                                            </IconButton>
                                          )}

                                          {/* Visible 4 emojis */}
                                          {visibleReplyEmojis.map((emoji) => (
                                            <IconButton
                                              key={emoji}
                                              size="small"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setEditText((prev) => safeDecode(prev + emoji));
                                              }}
                                              sx={{
                                                p: 0.5,
                                                width: 20,
                                                height: 20,
                                                color: "unset !important",
                                              }}
                                            >
                                              <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                                                {emoji}
                                              </Typography>
                                            </IconButton>
                                          ))}

                                          {/* Right arrow (show only if more remain) */}
                                          {replyEmojiIndex + visibleReplyEmojiCount < emojis.length && (
                                            <IconButton size="small" onClick={handleNextReplyEmoji} sx={{ p: 0 }}>
                                              <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                                                {">"}
                                              </Typography>
                                            </IconButton>
                                          )}
                                        </Box>
                                      )}
                                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                        <StyledTooltip title="Save">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              if (editTarget.type === "comment") {
                                                onEditComment(editTarget.commentUuid, editText);
                                              } else {
                                                onEditReply(editTarget.commentUuid, editTarget.replyUuid, editText);
                                              }
                                              setEditTarget(null);
                                            }}
                                            sx={{
                                              backgroundColor: "#E6F4EA",
                                              "&:hover": { backgroundColor: "#D4ECD8" },
                                            }}
                                          >
                                            <CheckIcon sx={{ fontSize: 20, color: "#1e7e34" }} />
                                          </IconButton>
                                        </StyledTooltip>
                                        <StyledTooltip title="Cancel">
                                          <IconButton
                                            size="small"
                                            onClick={() => setEditTarget(null)}
                                            sx={{
                                              backgroundColor: "#F8E6E6",
                                              "&:hover": { backgroundColor: "#F2D5D5" },
                                            }}
                                          >
                                            <CloseIcon sx={{ fontSize: 20, color: "#c62828" }} />
                                          </IconButton>
                                        </StyledTooltip>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box 
                                      sx={{ 
                                        display: "flex", 
                                        flexDirection: "row", 
                                        alignItems: "center" 
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          backgroundColor: "#F5F5F5",
                                          p: 1,
                                          borderRadius: "8px",
                                          width: isMobile ? "183px" : "440px",
                                        }}
                                      >
                                        <Typography 
                                          variant="caption"
                                          sx={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {safeDecode(reply.content)}
                                        </Typography>
                                      </Box>
                                      {reply.userId.username === loggedUserName && (
                                        <IconButton
                                          sx={{
                                            padding: "2px",
                                            ml: 0.25,
                                            width: "24px",
                                            height: "24px",
                                            borderRadius: "50%",
                                          }}
                                          onClick={(e) => {
                                            setSelected({
                                              type: "reply",
                                              commentUuid: c.uuid,
                                              replyUuid: reply.uuid,
                                            });
                                            setAnchorEl(e.currentTarget);
                                          }}
                                        >
                                          <MoreVertIcon 
                                            sx={{ fontSize: 16 }}                                      
                                          />
                                        </IconButton>
                                      )}
                                    </Box>
                                  )}                                  
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      gap: 1,                        
                                    }}
                                  >
                                    <Typography 
                                      variant="caption" 
                                      color="#9e9e9e"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {timeAgo(reply.createDate)}
                                    </Typography>
                                    <Box sx={{ mt: 1.5 }}> 
                                      <StyledDot size={5} color="#E0E0E0" />
                                    </Box>
                                    <Box sx={{ mt: 0, mr:"auto" }}> 
                                      <StyledTooltip
                                        title={reply.isLiked ? "Click to undo like" : "Click to like"}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() => 
                                            onCommentReplyLikeToggle(!reply.isLiked, c.uuid, reply.uuid)
                                          }
                                          aria-label={
                                            reply.isLiked ? "Remove from favorites" : "Add to favorites"
                                          }
                                          sx={{
                                            color: reply.isLiked ? "#EF5658" : "#9e9e9e",
                                            p: 0,
                                            pr: 0.5,
                                            "&:hover": {
                                              backgroundColor: "rgba(0,0,0,0.04)",
                                            },
                                          }}
                                        >
                                          {reply.isLiked ? (
                                            <FavoriteIcon sx={{ fontSize: "14px" }} />
                                          ) : (
                                            <FavoriteBorderOutlinedIcon sx={{ fontSize: "14px" }} />
                                          )}
                                        </IconButton>
                                      </StyledTooltip>                      
                                      <LikesPopup users={reply.likedUsers}>
                                        <Typography
                                          variant="caption"
                                          color="text.primary"
                                          sx={{ cursor: "pointer" }}
                                        >
                                          {reply.likeNo}
                                        </Typography>
                                      </LikesPopup>
                                    </Box>
                                  </Box>
                                </Box>                                
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>                      
                    </Box>
                    {replyingTo === c.uuid && (
                      <Box
                        sx={{ 
                          display: "flex",
                          flexDirection: "row",
                          ml: 6,
                          mt: 2,
                        }}
                      >
                        <Avatar 
                          src={loggedUserAvatar}
                        />
                        <Box 
                          sx={{ 
                            ml: 1,
                            position: "relative",
                            width: isMobile ? "193px" : "470px"
                          }}
                        >
                          <TextField                          
                            size="small"
                            multiline
                            minRows={1}
                            placeholder="Write a Reply..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            inputRef={(el) => (replyInputRefs.current[c.uuid] = el)} 
                            onKeyDown={(e) => {
                              if ((e.key === "Enter" && e.ctrlKey) || (e.key === "Enter" && e.metaKey)) {
                                e.preventDefault();
                                handleReplyPost(c.uuid);
                              }
                            }}
                            sx={{                            
                              width: isMobile ? "193px" : "470px",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backgroundColor: "#F5F5F5",
                                paddingRight: "30px", 
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "& .MuiInputBase-input": {
                                paddingBottom: "36px",
                              },
                              "& .MuiInputBase-input::placeholder": {
                                fontSize: "14px",
                              },
                              "@media (max-width:833px)": {
                                "& .MuiInputBase-input::placeholder": {
                                  fontSize: "12px",
                                },
                              },
                            }}
                          />
                          <Box                            
                            sx={{
                              position: "absolute",
                              top: "6px",
                              right: "8px",
                              cursor: "pointer",
                              color: "red",
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              userSelect: "none",
                              lineHeight: 1,
                              "&:hover": {
                                backgroundColor: "rgba(255,0,0,0.1)",
                              }
                            }}
                          >
                            <StyledTooltip title="Close">
                              <IconButton
                                size="small"
                                onClick={() => setReplyingTo(null)}
                                sx={{
                                  backgroundColor: "#F8E6E6",
                                  "&:hover": { backgroundColor: "#F2D5D5" },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 12, color: "#c62828" }} />
                              </IconButton>
                            </StyledTooltip>
                          </Box>
                          {/* Emoji Row (bottom-left inside the TextField) */}
                          {!isMobile ? (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: "6px",
                                left: "10px",
                                display: "flex",
                                gap: 1,
                                zIndex: 20,
                              }}
                            >
                              {emojis.map((emoji) => (
                                <IconButton
                                  key={emoji}
                                  size="small"
                                  onClick={(e) => handleReplyEmojiClick(emoji, e)}
                                  sx={{
                                    p: 0.5,
                                    width: 20,
                                    height: 20,
                                    color: "unset !important",          // remove MUI's gray filter
                                    opacity: 1,                          // force full brightness
                                    "&:hover": {
                                      backgroundColor: "transparent !important",
                                      opacity: 1,
                                    }
                                  }}
                                >
                                  <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                                    {emoji}
                                  </Typography>
                                </IconButton>
                              ))}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: "6px",
                                ml: "7px",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                zIndex: 20,
                                width: "80%", // prevents covering Post button
                              }}
                            >
                              {/* Left arrow (show only if not at start) */}
                              {replyEmojiIndex > 0 && (
                                <IconButton size="small" onClick={handlePrevReplyEmoji} sx={{ p: 0 }}>
                                  <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                                    {"<"}
                                  </Typography>
                                </IconButton>
                              )}

                              {/* Visible 4 emojis */}
                              {visibleReplyEmojis.map((emoji) => (
                                <IconButton
                                  key={emoji}
                                  size="small"
                                  onClick={(e) => handleReplyEmojiClick(emoji, e)}
                                  sx={{
                                    p: 0.5,
                                    width: 20,
                                    height: 20,
                                    color: "unset !important",
                                  }}
                                >
                                  <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                                    {emoji}
                                  </Typography>
                                </IconButton>
                              ))}

                              {/* Right arrow (show only if more remain) */}
                              {replyEmojiIndex + visibleReplyEmojiCount < emojis.length && (
                                <IconButton size="small" onClick={handleNextReplyEmoji} sx={{ p: 0 }}>
                                  <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                                    {">"}
                                  </Typography>
                                </IconButton>
                              )}
                            </Box>
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              handleReplyPost(c.uuid);
                            }}
                            sx={{
                              position: "absolute",
                              bottom: "6px",
                              right: "6px",
                              textTransform: "none",
                              borderRadius: "10px",
                              px: 2,
                              zIndex: 2100,
                            }}
                            disabled={!(replyText ?? "").trim()}
                          >
                            Post
                          </Button>
                        </Box>                      
                      </Box>
                    )}
                  </Box>
                </CommentBox>
                {index !== comments.length - 1 && (
                  <Divider sx={{ my: 3 }} />
                )}
              </Box>
            ))
          )}
          <div ref={commentsEndRef} />
        </Box>

        <Divider />

        {/* Comment Input */}
        <Box
          sx={{ 
            display: "flex",
            flexDirection: "row",
            m: 2,
          }}
        >
          <Avatar 
            //should be replaced with logged in user avatar 
            src={loggedUserAvatar}
          />
          <Box 
            sx={{ 
              ml: 1,
              position: "relative",
              width: isMobile ? "241px" : "518px"
            }}
          >
            <TextField                          
              size="small"
              multiline
              placeholder="Write a Comment..."
              value={commentText}                          
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" && e.ctrlKey) || (e.key === "Enter" && e.metaKey)) {
                  e.preventDefault();
                  handlePost();
                }
              }}
              sx={{ 
                width: isMobile ? "241px" : "518px",                           
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#F5F5F5",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiInputBase-input": {
                  paddingBottom: "36px",
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "14px",
                },
                "@media (max-width:833px)": {
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "12px",
                  },
                },
              }}
            />
            {/* Emoji Row (bottom-left inside the TextField) */}
            {!isMobile ? (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "6px",
                  left: "10px",
                  display: "flex",
                  gap: 1,
                  zIndex: 20,
                }}
              >
                {emojis.map((emoji) => (
                  <IconButton
                    key={emoji}
                    size="small"
                    onClick={(e) => handleCommentEmojiClick(emoji, e)}
                    sx={{
                      p: 0.5,
                      width: 20,
                      height: 20,
                      color: "unset !important",          // remove MUI's gray filter
                      opacity: 1,                          // force full brightness
                      "&:hover": {
                        backgroundColor: "transparent !important",
                        opacity: 1,
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                      {emoji}
                    </Typography>
                  </IconButton>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "6px",
                  ml: "7px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 20,
                  width: "80%", // prevents covering Post button
                }}
              >
              {/* Left arrow (show only if not at start) */}
              {commentEmojiIndex > 0 && (
                <IconButton size="small" onClick={handlePrevCommentEmoji} sx={{ p: 0 }}>
                  <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                    {"<"}
                  </Typography>
                </IconButton>
              )}

              {/* Visible 4 emojis */}
              {visibleCommentEmojis.map((emoji) => (
                <IconButton
                  key={emoji}
                  size="small"
                  onClick={(e) => handleCommentEmojiClick(emoji, e)}
                  sx={{
                    p: 0.5,
                    width: 20,
                    height: 20,
                    color: "unset !important",
                  }}
                >
                  <Typography sx={{ fontSize: "20px", lineHeight: 1 }}>
                    {emoji}
                  </Typography>
                </IconButton>
              ))}

              {/* Right arrow (show only if more remain) */}
              {commentEmojiIndex + visibleCommentEmojiCount < emojis.length && (
                <IconButton size="small" onClick={handleNextCommentEmoji} sx={{ p: 0 }}>
                  <Typography sx={{ fontSize: "14px", lineHeight: 1 }}>
                    {">"}
                  </Typography>
                </IconButton>
              )}
              </Box>              
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handlePost}
              sx={{
                position: "absolute",
                bottom: "6px",
                right: "6px",
                textTransform: "none",
                borderRadius: "10px",
                px: 2,
                zIndex: 2100,
              }}
              disabled={!(commentText ?? "").trim()}
            >
              Post
            </Button>
          </Box>                      
        </Box>
      </DialogContent>
      <Menu
        TransitionComponent={Grow}
        transitionDuration={0}   
        MenuListProps={{
          disablePadding: true,
          sx: {
            py: 0,
          },
        }}
        PaperProps={{
          sx: {
            width: 110,
            maxHeight: 88,
            overflow: "hidden",
          },
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "flip",
                enabled: false,
              },
              {
                name: "preventOverflow",
                enabled: false,
              },
            ],
          },
        }}       
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
         anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          sx={{
            py: "6px",
            px: "14px",
            minHeight: "auto",
            "&:first-of-type": {
              mt: 0,
            },
            "&:last-of-type": {
              mb: 0,
            },
          }}
          onClick={() => {
            setAnchorEl(null);
            setEditTarget(selected);

            let existingText = "";
            if (selected.type === "comment") {
              const comment = comments.find(c => c.uuid === selected.commentUuid);
              existingText = comment?.content || "";
            } else {
              const comment = comments.find(c => c.uuid === selected.commentUuid);
              const reply = comment?.replies?.find(r => r.uuid === selected.replyUuid);
              existingText = reply?.content || "";
            }

            // Put existing text into editText state
            setEditText(safeDecode(existingText));

            setTimeout(() => {
              const el = editInputRef.current;
              if (el) {
                el.focus();
                const len = el.value.length;
                el.setSelectionRange(existingText.length, existingText.length);
              }
            }, 0);
          }}
        >
          Edit
        </MenuItem>

        <MenuItem
          sx={{
            py: "6px",
            px: "14px",
            minHeight: "auto",
          }}
          onClick={() => {
            setAnchorEl(null);

            // Open confirmation popup
            setDeleteTarget(selected);
            setDeleteAlertOpen(true);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <AlertPopup
        open={deleteAlertOpen}
        onClose={() => setDeleteAlertOpen(false)}
        title={"Are you sure you want to delete this post?"}
        message=""
        confirmText="DELETE"
        cancelText="CANCEL"
        onConfirm={() => {
          if (!deleteTarget) return;

          if (deleteTarget.type === "comment") {
            onDeleteComment(deleteTarget.commentUuid);
          } else {
            onDeleteReply(deleteTarget.commentUuid, deleteTarget.replyUuid);
          }

          setDeleteTarget(null);
        }}
      />
    </Dialog>
  );
}
